import { useEffect, useRef, type FC } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Toast } from '@/components/notifications/Toast/Toast';
import IconCheckmarkCircle from '@/assets/icons/icon-checkmark-circle.svg?react';
import IconWifiOff from '@/assets/icons/icon-wifi-off.svg?react';
import { dismissToast } from '@/store/notifications/slice';
import { selectToasts } from '@/store/notifications/selectors';
import {
  STYLE_PANEL_UNSAVED_TOAST_TYPE,
  stylePanelSaveAndCloseRef,
  stylePanelDiscardAndCloseRef,
} from '@/utils/stylePanelGuard';
import {
  VIDEO_UPLOAD_TOAST_TYPE,
  videoUploadCancelMap,
} from '@/components/inspector-fields/VideoPreview/NodeVideoPreviewSettings';

export const ToastContainer: FC = () => {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector(selectToasts);

  const timeoutsRef = useRef<Record<number, number>>({});
  useEffect(() => {
    toasts.forEach((t) => {
      // Only auto-dismiss if autoDismiss is not explicitly false
      if (t.autoDismiss !== false && !timeoutsRef.current[t.id]) {
        timeoutsRef.current[t.id] = window.setTimeout(() => {
          dispatch(dismissToast(t.id));
          delete timeoutsRef.current[t.id];
        }, 3000);
      }
    });
    return () => {
      Object.values(timeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutsRef.current = {};
    };
  }, [toasts, dispatch]);

  if (toasts.length === 0) return null;

  // Always render all toasts centered at the top (top: 60px).
  const centerToasts = toasts;

  return (
    <>
      {/* Top-center stack (always) */}
      {centerToasts.length > 0 && (
        <div className="fixed top-[60px] left-1/2 z-[101000] flex -translate-x-1/2 transform flex-col gap-2">
          {centerToasts.map((t) => {
            const icon =
              t.icon === 'check'
                ? IconCheckmarkCircle
                : t.icon === 'network-lost'
                  ? IconWifiOff
                  : undefined;
            const isStylePanelUnsavedToast = t.toastType === STYLE_PANEL_UNSAVED_TOAST_TYPE;

            let onButtonPrimaryClick: (() => void) | (() => Promise<void>) | undefined;
            if (isStylePanelUnsavedToast) {
              onButtonPrimaryClick = async () => {
                await stylePanelSaveAndCloseRef.current?.();
                dispatch(dismissToast(t.id));
              };
            } else if (t.buttonPrimaryHref) {
              onButtonPrimaryClick = () => {
                window.open(t.buttonPrimaryHref!, '_blank');
                dispatch(dismissToast(t.id));
              };
            }

            let onButtonSecondaryClick: (() => void) | undefined;
            if (isStylePanelUnsavedToast) {
              onButtonSecondaryClick = () => {
                stylePanelDiscardAndCloseRef.current?.();
                dispatch(dismissToast(t.id));
              };
            } else if (t.toastType === VIDEO_UPLOAD_TOAST_TYPE) {
              onButtonSecondaryClick = () => {
                // WHY: Call the first available cancel handler — only one video upload toast
                // exists at a time since showToast replaces all toasts.
                const cancelHandler = videoUploadCancelMap.values().next().value;
                cancelHandler?.();
                dispatch(dismissToast(t.id));
              };
            }

            return (
              <Toast
                key={t.id}
                text={t.text}
                state={t.state}
                icon={icon}
                loading={t.loading}
                buttonSecondaryText={t.buttonSecondaryText}
                buttonPrimaryText={t.buttonPrimaryText}
                onButtonPrimaryClick={onButtonPrimaryClick}
                onButtonSecondaryClick={onButtonSecondaryClick}
                showCloseButton={!!t.showCloseButton}
                onClose={() => {
                  dispatch(dismissToast(t.id));
                }}
              />
            );
          })}
        </div>
      )}
    </>
  );
};

export default ToastContainer;
